import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getIdToken } from "@/lib/firebase";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Archive, Bell, Check, MessageCircle, Store, Users } from "lucide-react";
import { Link, useLocation } from "wouter";

interface NotificationItem {
  id: number;
  type: string;
  category?: string | null;
  title: string;
  body: string;
  action_url?: string | null;
  action_label?: string | null;
  priority?: string | null;
  related_content_type?: string | null;
  related_content_id?: string | null;
  is_read?: number | boolean;
  read_at?: string | null;
  is_archived?: number | boolean;
  created_at?: string | null;
}

function isRead(notification: NotificationItem) {
  return notification.is_read === true || notification.is_read === 1;
}

function getNotificationHref(notification: NotificationItem) {
  if (notification.action_url) return notification.action_url;

  const entityType = notification.related_content_type;
  const entityId = notification.related_content_id;
  if (!entityType) return null;

  // All current notification producers (functions/utils/unified-notifications.ts
  // callers in comments/index.ts, market/contacts.ts, businesses/[id]/inquiries.ts,
  // meetings/[id]/join.ts, admin/business-*) always set action_url explicitly, so
  // this fallback rarely runs today - but it previously listed values
  // ("market", "talk", "business") that don't match what's actually stored
  // ("market_listing", "community_talk", "business_inquiry", etc.), so it would
  // have silently returned null (no link) the moment a producer stopped setting
  // action_url. Aligned with the real relatedContentType strings in use.
  switch (entityType) {
    case "market":
    case "market_listing":
      return entityId ? `/market/${entityId}` : "/market";
    case "market_contact_request":
      // relatedContentId here is the contact-request row id, not a listing id -
      // there's no valid /market/:id to build from it.
      return "/market";
    case "talk":
    case "community-talk":
    case "community_talk":
      return entityId ? `/community-talk/${entityId}` : "/community-talk";
    case "meeting":
    case "meetup":
      return entityId ? `/meetings/${entityId}` : "/meetings";
    case "business":
    case "business_inquiry":
    case "business_claim":
    case "business_media":
      return entityId ? `/business-directory/${entityId}` : "/business-directory";
    default:
      return null;
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case "market":
      return Store;
    case "meeting":
      return Users;
    case "business":
      return Store;
    case "dm":
      return MessageCircle;
    default:
      return Bell;
  }
}

async function fetchWithAuth(path: string, init?: RequestInit) {
  const token = await getIdToken();
  if (!token) throw new Error("로그인이 필요합니다");

  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed: ${response.status}`);
  }

  return response;
}

export default function Notifications() {
  const { isAuthenticated, loading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const {
    data: notifications = [],
    isLoading,
    error,
  } = useQuery<NotificationItem[]>({
    queryKey: ["/api/notifications"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await fetchWithAuth("/api/notifications?limit=50");
      const payload = await response.json();
      return Array.isArray(payload.items) ? payload.items : [];
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      await fetchWithAuth(`/api/notifications/${id}/read`, {
        method: "PATCH",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications/unread-count"],
      });
    },
    onError: (mutationError: Error) => {
      toast({
        title: "읽음 처리 실패",
        description: mutationError.message,
        variant: "destructive",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: number) => {
      await fetchWithAuth(`/api/notifications/${id}/archive`, {
        method: "PATCH",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications/unread-count"],
      });
    },
    onError: (mutationError: Error) => {
      toast({
        title: "보관 실패",
        description: mutationError.message,
        variant: "destructive",
      });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await fetchWithAuth("/api/notifications/mark-all-read", {
        method: "PATCH",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications/unread-count"],
      });
    },
    onError: (mutationError: Error) => {
      toast({
        title: "전체 읽음 처리 실패",
        description: mutationError.message,
        variant: "destructive",
      });
    },
  });

  const visibleNotifications = notifications.filter(
    (item) => item.is_archived !== true && item.is_archived !== 1,
  );
  const unreadCount = visibleNotifications.filter((item) => !isRead(item)).length;

  return (
    <>
      <Seo
        title="알림 — OZK Local"
        description="댓글, 마켓 문의, 모임 참여, 업체 문의 알림을 확인하세요."
        path="/notifications"
      />

      <main className="min-h-screen bg-background">
        <div className="mx-auto max-w-3xl px-4 py-6 md:py-10">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                OZK Local
              </p>
              <h1 className="text-2xl font-bold text-foreground">알림</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                읽지 않은 알림 {unreadCount}개
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                >
                  전체 읽음
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/")}>
                홈으로
              </Button>
            </div>
          </div>

          {loading ? (
            <Card className="p-5">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="mt-3 h-4 w-64" />
            </Card>
          ) : !isAuthenticated ? (
            <Card className="p-6 text-center">
              <Bell className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <h2 className="font-semibold text-foreground">
                로그인이 필요합니다
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                알림은 로그인한 사용자에게만 표시됩니다.
              </p>
            </Card>
          ) : isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((item) => (
                <Card key={item} className="p-4">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="mt-2 h-4 w-full" />
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card className="p-6">
              <h2 className="font-semibold text-foreground">
                알림을 불러오지 못했습니다
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                알림 테이블 또는 인증 경계가 아직 준비되지 않았을 수 있습니다.
              </p>
            </Card>
          ) : visibleNotifications.length === 0 ? (
            <Card className="p-6 text-center">
              <Bell className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <h2 className="font-semibold text-foreground">
                아직 알림이 없습니다
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                댓글, 문의, 모임 참여가 생기면 이곳에 표시됩니다.
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {visibleNotifications.map((notification) => {
                const Icon = getTypeIcon(notification.type);
                const href = getNotificationHref(notification);
                const read = isRead(notification);

                const content = (
                  <Card
                    className={`p-4 transition-colors ${
                      read ? "bg-card" : "border-[#663366] bg-[#663366]/5"
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="mt-1 h-9 w-9 shrink-0 rounded-md bg-[#663366]/10 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-[#663366]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h2 className="font-semibold text-foreground">
                              {notification.title}
                            </h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {notification.body}
                            </p>
                            {notification.created_at && (
                              <p className="mt-2 text-xs text-muted-foreground">
                                {new Date(notification.created_at).toLocaleString("ko-KR")}
                              </p>
                            )}
                          </div>
                          {!read && (
                            <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#663366]" />
                          )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {!read && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                markReadMutation.mutate(notification.id);
                              }}
                            >
                              <Check className="mr-1 h-4 w-4" />
                              읽음
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              archiveMutation.mutate(notification.id);
                            }}
                          >
                            <Archive className="mr-1 h-4 w-4" />
                            보관
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );

                return href ? (
                  <Link key={notification.id} href={href}>
                    {content}
                  </Link>
                ) : (
                  <div key={notification.id}>{content}</div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
